import Loader from '@/shared/components/Loader';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
import {
  BIDDER_ID,
  BODY1,
  defaultPage,
  defaultPageSize,
  HASH,
  PAID,
  PAY_STATUS,
  TABLE,
  TABLE_NAME,
  NAME,
  PAYMENT_PENDING,
  QUANTITY,
  NO_CONTRIBUTION,
} from '@/utils/constants';
import {
  Box,
  Drawer,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {ReactComponent as UpDownIcon} from '@/assets/icons/arrow-up-down.svg';
import {ReactComponent as EllipseIcon} from '@/assets/icons/ellipse-green.svg';
import ActionsMenu from '@/shared/components/Table/ActionMenu';
import {PAYMENT_STATUS, ROUTER_URL, TABLES_ENTITIES} from '@/utils/enums';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {useNavigate} from 'react-router-dom';
import {IFilterGroup} from '@/utils/types';
import SearchHeader from '@/shared/components/Table/SearchHeader';
import {
  getAttendee,
  getAttendeeSelector,
  getAttendeesList,
  getAttendeesListSelector,
} from '@/store/organization-attendees';
import {getErrorMessage, getTableStatusStyle} from '@/utils/helpers';
import {style} from '@/pages/Organization/CheckOut/CheckoutStyle.styles';
import {useEffect, useState} from 'react';
import {filterMenuStyle} from '@/shared/components/Table/FilterMenu/FilterMenu.styles';
import FilterContent from '@/pages/Admin/Events/FilterContent';
import {toast} from 'react-toastify';
import {
  attendeesDefaultFilter,
  attendeesDefaultPaidStatusFilterValues,
  getUpdatedFilterData,
} from '../../Attendees/AttendeesTable/helpers';
import Pagination from '@/shared/components/Pagination';
import {getCurrentEventSelector} from '@/store/organization-event';
import Modal from '@/shared/components/Modal';
import AttendeeDetails from '../../Attendees/AttendeeDetails';
import {attendeesTableStyle} from '../../Attendees/AttendeesTable/AttendeesTable.styles';
import {currencyFormatter} from '@/utils/helpers/currency';

const CheckOutTable = ({isDataLoaded}: {isDataLoaded: boolean}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isMobile = useMediaQuery('(max-width:767.98px)');
  const isLaptop = useMediaQuery('(min-width:1200px)');
  const isTablet = useMediaQuery('(min-width:768px) and (max-width:1199.98px)');

  const currentEvent = useAppSelector(getCurrentEventSelector);

  const attendeesList = useAppSelector(getAttendeesListSelector);
  const attendee = useAppSelector(getAttendeeSelector);

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [isProfilePage, setIsProfilePage] = useState<boolean>(false);
  const [isShowAttendeeDetailsModal, setIsShowAttendeeDetailsModal] = useState<boolean>(false);
  const [filterData, setFilterData] = useState<IFilterGroup[]>(attendeesDefaultPaidStatusFilterValues);
  const [filterValues, setFilterValues] = useState(attendeesDefaultFilter);

  const [page, setPage] = useState<number>(defaultPage);

  const getPayStatusSx = (payStatus: string) => getTableStatusStyle(payStatus);

  const handleSortChange = (newSortColumn: string) => {
    const isAsc = filterValues.sortColumn !== newSortColumn || !filterValues.isOrderDesc;

    setFilterValues((prevState: any) => ({
      ...prevState,
      isOrderDesc: isAsc,
      sortColumn: newSortColumn,
    }));
  };

  const handleOpenPopup = () => setIsPopupOpen(!isPopupOpen);
  const handleProfileTabStyles = (isProfilePage: boolean) => setIsProfilePage(isProfilePage);

  const updateFilterData = (groupTitle: string, optionTitle: string, id?: number) => {
    return setFilterData((prevFilterData) => getUpdatedFilterData(prevFilterData, groupTitle, optionTitle, id));
  };

  const handleFilterChange = (groupTitle: string, optionTitle: string, id?: number) => {
    updateFilterData(groupTitle, optionTitle, id);
    setPage(1);

    switch (groupTitle) {
      case PAY_STATUS:
        if (optionTitle === 'All Attendees') {
          setFilterValues((prevState) => ({...prevState, attendeePaidStatus: PAYMENT_STATUS.ALL}));
        } else {
          setFilterValues((prevState) => ({
            ...prevState,
            attendeePaidStatus: optionTitle as PAYMENT_STATUS,
          }));
        }
        break;
      default:
        break;
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setPage(1);
  };

  const handleChangeAttendeeDetailsModal = async (attendeeId?: number) => {
    if (isTablet) {
      navigate(`/organization/attendees/${attendeeId}`);

      return;
    }

    setIsShowAttendeeDetailsModal(!isShowAttendeeDetailsModal);

    if (!isShowAttendeeDetailsModal && attendeeId) {
      try {
        await dispatch(getAttendee(attendeeId)).unwrap();
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    }
  };
  const getAuctionListData = async () => {
    try {
      const reqBody = {
        eventId: currentEvent?.id as number,
        page: isLaptop ? page : 1,
        pageSize: isLaptop ? defaultPageSize : 10000,
        searchName: searchValue,
				hasContribution: true,
        ...filterValues,
      };

      await dispatch(getAttendeesList(reqBody)).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  useEffect(() => {
    if (currentEvent?.id) getAuctionListData();
  }, [currentEvent?.id, filterValues, searchValue, page]);

  return (
    <Box sx={style.wrapper}>
      <SearchHeader
        page={page}
        items={attendeesList!.totalCount < defaultPageSize ? attendeesList!.totalCount : defaultPageSize}
        total={attendeesList.totalCount}
        searchPlaceholder="Search for attendee"
        onPressEnter={() => {}}
        onSearchChange={handleSearchChange}
        parentClassName={{padding: '0 16px'}}
        filterOption={filterData}
        onFilterChange={handleFilterChange}
        onOpenDropdown={handleOpenPopup}
        isMobileMode={isMobile}
        unitName={TABLES_ENTITIES.GUESTS}
      />
      {isDataLoaded ? (
        attendeesList?.totalCount ? (
          <Box sx={style.tableContent}>
            <TableContainer sx={style.container}>
              <Table sx={style.table}>
                <TableHead>
                  <TableRow sx={style.tableHeader}>
                    <TableCell>
                      {BIDDER_ID}
                      {HASH}
                    </TableCell>

                    <TableCell>
                      <TableSortLabel active onClick={() => handleSortChange('firstName')} IconComponent={UpDownIcon}>
                        {NAME}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell>
                      <TableSortLabel active onClick={() => handleSortChange('tableNumber')} IconComponent={UpDownIcon}>
                        {TABLE}
                        {HASH}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell>
                      <TableSortLabel active onClick={() => handleSortChange('tableName')} IconComponent={UpDownIcon}>
                        {TABLE_NAME}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell>{QUANTITY}</TableCell>

                    <TableCell>{PAY_STATUS}</TableCell>

                    <TableCell />
                  </TableRow>
                </TableHead>

                <TableBody>
                  {attendeesList?.data.map((row) => (
                    <TableRow
                      key={row.id}
                      sx={style.tableBody}
                      onClick={() => {
                        if (!isLaptop) navigate(`${ROUTER_URL.ORGANIZATION_ATTENDEES}/${row.id}`);
                      }}
                    >
                      <TableCell>
                        {HASH}
                        {row.id}
                      </TableCell>

                      <TableCell>
                        {row.isUnknown
                          ? `Guest of ${row.table.purchaser.firstName} ${row.table.purchaser.lastName}`
                          : `${row.userInfo.firstName} ${row.userInfo.lastName}`}
                        <Typography>{isMobile && `${HASH} ${row.table.tickets[0]?.id}`}</Typography>
                      </TableCell>

                      <TableCell>{row.table.number}</TableCell>

                      <TableCell>
                        <Box>
                          <Typography>{row.table.name}</Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box>
                          <Typography>{currencyFormatter.format(row.totalAmount)}</Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography
                          sx={{
                            ...style.status,
                            ...getPayStatusSx(
                              row.paidStatus === null
                                ? NO_CONTRIBUTION
                                : row.paidStatus === PAYMENT_STATUS.PENDING
                                ? PAYMENT_PENDING
                                : PAID
                            ),
                          }}
                        >
                          {row.paidStatus === null ? '-' : row.paidStatus === PAID ? PAID : PAYMENT_PENDING}
                          {row.paidStatus && <EllipseIcon />}
                        </Typography>
                      </TableCell>

                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <ActionsMenu
                          disableRipple
                          content={
                            <Box>
                              <Typography
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleChangeAttendeeDetailsModal(row.id);
                                }}
                                variant={BODY1}
                                sx={style.menuItem}
                              >
                                Go to profile
                              </Typography>
                            </Box>
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {attendeesList?.totalCount && (
              <Box sx={style.pagination}>
                <Pagination
                  page={page}
                  pageSize={attendeesList.pageSize}
                  total={attendeesList?.totalCount}
                  onChange={(step) => setPage(step)}
                />
              </Box>
            )}
          </Box>
        ) : (
          <EmptyTableData />
        )
      ) : (
        <Loader parentClassName={style.loader} size={50} />
      )}

      <Drawer anchor="bottom" open={isPopupOpen} onClose={handleOpenPopup}>
        <Box>
          <Typography sx={{p: 2, fontWeight: 600, fontSize: 16}}>Filters</Typography>
          <Box sx={filterMenuStyle.wrapper}>
            <FilterContent options={filterData} onFilterChange={handleFilterChange} />
          </Box>
        </Box>
      </Drawer>

      <Modal
        onClose={() => setIsShowAttendeeDetailsModal(false)}
        open={isShowAttendeeDetailsModal && !!attendee}
        sx={{...attendeesTableStyle.detailsModal, ...(isProfilePage && attendeesTableStyle.profilePage)}}
      >
        <AttendeeDetails
          onCloseModal={handleChangeAttendeeDetailsModal}
          handleProfileTabStyles={handleProfileTabStyles}
        />
      </Modal>
    </Box>
  );
};

export default CheckOutTable;
